/**
 * @copyright Copyright 2023-2024 Epic Systems Corporation
 * @file Information banner text for apps
 * @module Epic.AppOrchard.Showroom.AppBannerSection
 */

import { Box } from "@chakra-ui/react";
import { config } from "ao/appConfig";
import { ApiLicenseTermsBanner, StatusAlert } from "ao/components/Core";
import { DownloadApprovedStatus } from "ao/data";
import { IUserSecurity } from "ao/types";
import { IShowroomAppDownloadModel } from "ao/types/showroom";
import { FunctionComponent, memo } from "react";

interface IProps {
	appDetails: IShowroomAppDownloadModel;
	userSecurity: IUserSecurity;
	setAppDetails: (x: IShowroomAppDownloadModel) => void;
	isShowroom: boolean;
}

export const AppBannerSection: FunctionComponent<IProps> = memo((props) => {
	const { appDetails, userSecurity, setAppDetails, isShowroom } = props;

	return (
		<Box my="10px">
			{!appDetails.orgHasPurchaser && (
				<StatusAlert
					status="warning"
					message={
						userSecurity.canManageUsers
							? `Your organization doesn't have anyone with the Can Purchase Apps permission which allows managing requests for new products from this site. You can give someone the Can Purchase Apps permission in the Organization Details page in ${config.SiteName}.`
							: "Your organization doesn't have anyone with the Can Purchase Apps permission which allows managing requests for new products from this site. To get someone this permission, contact your Epic representative."
					}
				/>
			)}
			{userSecurity.canPurchase &&
				!appDetails.previousAPILicenseExists &&
				!appDetails.isUSCDIDownloadSet && (
					<ApiLicenseTermsBanner
						mt="0.5em"
						mb="0.5em"
						apiLicenseAgreementId={appDetails.apiLicenseTermsId}
						userSecurity={userSecurity}
						successHandler={() =>
							setAppDetails({
								...appDetails,
								previousAPILicenseExists: true,
							})
						}
						isShowroom={isShowroom}
					/>
				)}
			{appDetails.vendorApproved === DownloadApprovedStatus.Approved && appDetails.isSyncDisabled && (
				<StatusAlert
					status="warning"
					message={
						"Syncing to your Production system has been temporarily disabled for this app by Epic while the app is modified. There may be some functionality described in this listing that has not yet synced to your Production system. Contact your Epic representative and mention SLG 9084550 if you have any questions."
					}
				/>
			)}
		</Box>
	);
});
